import BootstrapTable from 'react-bootstrap-table-next';
import cn from 'classnames';
import { isArray, upperFirst } from 'lodash';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { getDynamicColumn, sortCaret } from '../../../utils/tableHelper';
import { getStockInRetailer } from '../../../utils/common';
import { TAB_DROPSHIP, TAB_REORDER } from '../../../constants/mainConstants';
import { variantSort2 } from '../../../utils/sortHelper';

import Button from '../../common/Button';
import ModalWindow from '../../common/Modal';
import Switch from '../../common/Switch';

import styles from './ApproveVariantsModal.module.scss';
import { ActiveStatus } from '../../../constants/activeStatus';
import InventoryTableHeaderWithTooltip from '../../tables/pricing/inventory/invetory-table-header-with-tooltip';

function ApproveVariantsModal(props) {
  const { current: currentRetailer, warehouses: enabledWarehouses } = useSelector((state) => state.retailer);
  const { currentFilters } = useSelector((state) => state.productFilters);
  const {
    productDetails: { variants },
    approve,
    unapprove,
    approved,
    open,
    onClose,
    permissionType,
    isVerboseInventoryInfo,
    shouldAllowUserToDoActions = true,
  } = props;

  const isFilteringArchived = useMemo(() => {
    return currentFilters?.activeProducts === ActiveStatus.ARCHIVED;
  }, [currentFilters]);

  if (!variants) {
    return null;
  }

  const toggleSwitchAll = (value) => {
    if (variants) {
      const variantsIds = variants.map(({ id }) => id);
      toggleSwitch(value, variantsIds);
    }
  };

  const isSwitchedAll = () => {
    return variants.every((variant) => approved.indexOf(variant.id) >= 0);
  };

  const toggleSwitch = (value, id) => {
    const ids = isArray(id) ? id : [id];
    if (value) {
      approve(ids);
    } else {
      unapprove(ids);
    }
  };

  const productInfo = () => {
    const { product } = props;

    return (
      <div className={styles.product}>
        <div className={styles.img_block}>
          <img alt={product.title} src={product.images[0]} />
        </div>

        <div className={styles.description}>
          <p className={styles.pt}>{product.title}</p>
        </div>

        <div className={styles.details}>
          <p>
            {product.available_inventory} units - {product.variants.length} variants
          </p>
          <p>Approved to {product.retailers?.length || 0} retailers</p>
          <p className={styles.model}>Model: {product.model}</p>
        </div>
      </div>
    );
  };

  const saveModal = () => {
    props.saveVariants();
    onClose();
  };

  const formatStatus = (cell, row, rowIndex, { approved, toggleSwitch }) => {
    const isSwitched = approved.includes(row.id);
    return <Switch checked={isSwitched} onSwitch={(value) => toggleSwitch(value, row.id)} />;
  };

  const formatDynamicColumn = (cell, row, rowIndex, field) => {
    return row.options[field] ? <span>{row.options[field]}</span> : <span>-</span>;
  };

  const columns = [];
  const hasPermissions = variants.some((variant) => variant.permissions && variant.permissions.length > 0);
  const hasUpc = variants.some((variant) => !!variant.upc);
  const hasEan = variants.some((variant) => !!variant.ean);
  const dynamicColumns = getDynamicColumn(variants);

  const shouldDisableActions = !shouldAllowUserToDoActions || isFilteringArchived;

  if (!shouldDisableActions) {
    columns.push({
      text: 'Id',
      dataField: 'id',
      formatter: formatStatus,
      formatExtraData: {
        approved,
        toggleSwitch,
      },
      headerFormatter: () => <Switch checked={isSwitchedAll()} onSwitch={toggleSwitchAll} />,
      isDummyField: true,
      classes: 'narrow',
      headerClasses: 'narrow',
    });
  }
  dynamicColumns.forEach((field, i) => {
    columns.push({
      text: upperFirst(field),
      dataField: field,
      formatter: formatDynamicColumn,
      formatExtraData: field,
      sort: true,
    });
  });

  const unitsHeaderText = `Units${isVerboseInventoryInfo ? ' in Cymbio' : ''}`;
  const stockColumns = [
    {
      text: unitsHeaderText,
      dataField: 'available_inventory',
      sort: true,
      headerFormatter: (column, columnIndex, options) => (
        <InventoryTableHeaderWithTooltip
          text={unitsHeaderText}
          tooltip="Reflects only enabled warehouses for this retailer"
          sortElement={options.sortElement}
        ></InventoryTableHeaderWithTooltip>
      ),
    },
  ];

  if (isVerboseInventoryInfo) {
    stockColumns.push(
      {
        text: 'Buffer',
        dataField: 'inventory_buffer',
      },
      {
        text: `Units Sent to Retailer`,
        headerFormatter: () => (
          <OverlayTrigger overlay={<Tooltip>Net of any caps, buffers and other settings</Tooltip>}>
            <span>Units Sent to Retailer</span>
          </OverlayTrigger>
        ),
        dataField: 'inventory_in_retailer',
        formatter: (_, row) => {
          const { permissions, inventories, inventory_buffer } = row;
          const { max_inventory_visible, approved_warehouses: approvedWarehouseIds } = currentRetailer;
          const availableInventory = inventories
            .filter((inventory) => {
              if (approvedWarehouseIds && approvedWarehouseIds.length > 0) {
                // supplier selected specific warehouses
                return approvedWarehouseIds.includes(inventory.warehouse_id);
              }

              // supplier did not select specific warehouses, so check if it is enabled
              return enabledWarehouses.map(({ id }) => id).includes(inventory.warehouse_id);
            })
            .reduce((sumInventory, inventory) => sumInventory + inventory.amount, 0);

          return getStockInRetailer({
            availableInventory: availableInventory,
            permissions: permissions,
            inventoryBuffer: inventory_buffer,
            maxVisibleInventory: max_inventory_visible,
          });
        },
      },
    );
  }

  columns.push(...stockColumns, {
    text: 'SKU',
    dataField: 'sku',
  });

  if (hasUpc) {
    columns.push({
      text: 'UPC',
      dataField: 'upc',
      sort: true,
    });
  }

  if (hasEan) {
    columns.push({
      text: 'EAN',
      dataField: 'ean',
      sort: true,
    });
  }

  const wrapperClasses = [styles.table, styles[`table-${permissionType}`]];

  if (permissionType === TAB_DROPSHIP && hasPermissions) {
    wrapperClasses.push(styles.tableWide);
  }

  return (
    <ModalWindow open={open} onClose={onClose} size="xlarge">
      <h2>{shouldDisableActions ? `Product Details` : `Approve Variants`}</h2>
      <hr />

      <div className={styles.body}>
        {productInfo()}

        <div className={cn(wrapperClasses)}>
          <BootstrapTable
            data={variants}
            columns={columns}
            keyField="id"
            printable
            striped
            hover
            bordered={false}
            defaultSorted={[
              {
                dataField: dynamicColumns[0] || 'available_inventory',
                order: 'asc',
              },
            ]}
            defaultSortDirection="asc"
            sort={{
              sortCaret: sortCaret,
              sortFunc: variantSort2,
            }}
          ></BootstrapTable>
        </div>
      </div>
      <hr />

      {!shouldDisableActions ? (
        <div className={styles.footer}>
          <Button color="white" text="CANCEL" onClick={onClose} />

          <Button text="SAVE" onClick={saveModal} disabled={!approved.length} />
        </div>
      ) : (
        <></>
      )}
    </ModalWindow>
  );
}

export default ApproveVariantsModal;

ApproveVariantsModal.defaultProps = {
  open: false,
  permissionType: undefined,
};

ApproveVariantsModal.propTypes = {
  approved: PropTypes.arrayOf(PropTypes.number).isRequired,
  product: PropTypes.object.isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  productDetails: PropTypes.object.isRequired,
  approve: PropTypes.func.isRequired,
  unapprove: PropTypes.func.isRequired,
  saveVariants: PropTypes.func.isRequired,
  permissionType: PropTypes.oneOf([TAB_DROPSHIP, TAB_REORDER]),
};
